import React, {
    useCallback,
    useEffect,
    useState,
} from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import {
    CircularProgress,
    Typography,
} from '@mui/material'

import { useTranslation } from 'shared/hooks/useSafeTranslation'
import Loading from 'shared/Loading'
import PreLoadScannerProvider from 'shared/QRScanner/PreLoadScanner'
import ROUTE_KEYS from 'shared/utils/routeKeys'
import useStyle from 'shared/hooks/useStyle'

import useLocation from '../hook/useLocation'
import useDebouncedValue from '../hook/useDebouncedValue'
import useLoadMoreOnScroll from '../hook/useLoadMoreOnScroll'
import useLocationContext from '../LocationProvider/useLocationContext'

import CenterSearch from './CenterSearch'
import styles from './CentersDashboard.style'

const SEARCH_DEBOUNCE_MS = 300

const propTypes = { children: PropTypes.element.isRequired }

function CentersDashboard({ children }) {
    const { t } = useTranslation()
    const classes = useStyle(styles)
    const navigate = useNavigate()
    const {
        location,
        setLocation,
    } = useLocationContext()
    const [
        searchInput,
        setSearchInput,
    ] = useState('')
    const [
        listElement,
        setListElement,
    ] = useState(null)
    const searchPhrase = useDebouncedValue(searchInput.trim(), SEARCH_DEBOUNCE_MS)
    const {
        data: centers = [],
        isLoading,
        isFetching,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        error,
    } = useLocation(Boolean(!location.locationId), searchPhrase)
    const loadMoreRef = useLoadMoreOnScroll({
        onLoadMore: fetchNextPage,
        isEnabled: Boolean(hasNextPage) && !isFetching,
        scrollContainer: listElement,
    })

    const onCenterSelect = useCallback((center) => {
        const contextValue = {
            locationName: center.name,
            locationId: center.id,
        }

        setLocation(contextValue)
        navigate(ROUTE_KEYS.MAIN)
    }, [
        setLocation,
        navigate,
    ])

    useEffect(() => {
        if (listElement) {
            listElement.scrollTop = 0
        }
    }, [
        listElement,
        searchPhrase,
    ])

    useEffect(() => {
        if (!location.locationId) {
            navigate(ROUTE_KEYS.MAIN)
        }
    }, [
        navigate,
        location.locationId,
    ])

    if (isLoading) {
        return (<Loading />)
    }
    if (error) {
        throw error
    }

    if (location.locationId) {
        return (
            <PreLoadScannerProvider>
                {children}
            </PreLoadScannerProvider>
        )
    }

    return (
        <div css={classes.root}>
            <div css={classes.main}>
                <div css={classes.header}>
                    <Typography
                        variant="h1"
                        css={classes.userLocationText}
                    >
                        {t('SELECT_SERVICE_CENTER')}
                    </Typography>
                    <CenterSearch
                        value={searchInput}
                        onChange={setSearchInput}
                        isSearching={isFetching && !isFetchingNextPage}
                    />
                </div>
                <div
                    css={classes.container}
                    ref={setListElement}
                >
                    {
                        centers.map((center) => {
                            return (
                                <div
                                    key={center.id}
                                    data-testid="service-center-item"
                                    css={classes.center}
                                    onClick={() => { onCenterSelect(center) }}
                                    aria-hidden="true"
                                >
                                    <Typography
                                        variant="h3"
                                        css={classes.centerText}
                                    >
                                        {center.name}
                                    </Typography>
                                </div>
                            )
                        })
                    }
                    {!isFetching && centers.length === 0 && (
                        <Typography
                            variant="h3"
                            css={classes.emptyState}
                        >
                            {t('NO_SERVICE_CENTERS_FOUND')}
                        </Typography>
                    )}
                    {isFetchingNextPage && (
                        <div
                            css={classes.inlineLoader}
                            data-testid="service-centers-inline-loader"
                        >
                            <CircularProgress size={32} />
                        </div>
                    )}
                    <div
                        ref={loadMoreRef}
                        data-testid="service-centers-load-more"
                    />
                </div>
            </div>
        </div>
    )
}

CentersDashboard.propTypes = propTypes

export default CentersDashboard
